import { Flex, List, Text, ListItem, Box } from "@chakra-ui/react";
import { trackEvent, identifyUser, trackConversion } from "lib/analytics";
import React, { useState } from "react";
import { downloadUrl } from "utils";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import Image from "next/image";
import Balancer from "react-wrap-balancer";
import { HubSpotForm } from "components/hubspot";
import { Link } from "ui/link";

type DownloadWhitepaperProps = {
  variant: "short" | "long";
  hubSpotFormId: string;
  title: string;
  fileUrl: string;
  fileName: string;
  posterImageUrl: string;
  variantContent: string;
};

export function DownloadWhitepaper({
  variant,
  hubSpotFormId,
  title,
  fileUrl,
  fileName,
  posterImageUrl,
  variantContent,
}: DownloadWhitepaperProps) {
  const [success, setSuccess] = useState(false);

  const downloadFileUrl = `${fileUrl}?dl=${fileName}.pdf`;

  const onFormSubmit = (lead: Record<string, string>) => {
    trackEvent(`${title} Downloaded`, { Email: lead.email }); // Keep legacy pattern of tracking download events by whitepaper title for now
    trackEvent("Whitepaper Downloaded", { Email: lead.email, file: title });
    identifyUser({ email: lead.email });
    trackConversion("asset-downloaded");

    downloadUrl(downloadFileUrl, `${fileName}.pdf`);

    setSuccess(true);
  };

  const posterImage = (
    <Image
      src={`${posterImageUrl}?w=400`}
      alt={`Preview of ${title}.`}
      width={200}
      height={300}
      style={{
        width: "100%",
        objectFit: "contain",
        height: "auto",
        maxHeight: variant === "long" ? "350px" : "250px",
      }}
    />
  );

  return (
    <Flex
      alignItems="center"
      gap={{ base: 6, md: 10 }}
      bg="brand.backgroundSecondary.light"
      borderRadius="base"
      my={16}
      px={{ base: 6, md: 8 }}
      py={{ base: 8, md: 10 }}
    >
      <Box flexGrow={{ md: 1 }} width={{ base: "100%", md: "auto" }}>
        <ReactMarkdown
          components={{
            li: ({ children }) => <ListItem textStyle="article.listItem">{children}</ListItem>,
            ul: ({ children }) => (
              <List textStyle="article.list" listStyleType="disc">
                {children}
              </List>
            ),
            /** We've asked marketing not to use h1 styles anymore to avoid a11y confusion, but this will stay for backfilling */
            h1: ({ children }) => (
              <Text position="relative" as="h2" textStyle={{ base: "article.h3", md: "article.h2" }} mt="0">
                <Balancer>{children}</Balancer>
              </Text>
            ),
            h2: ({ children }) => (
              <Text as="h2" textStyle="article.h3" scrollMarginTop={32} my={6} mt="0">
                <Balancer>{children}</Balancer>
              </Text>
            ),
            p: ({ children }) => <Text textStyle="article.p">{children}</Text>,
            strong: ({ children }) => (
              <Text as="strong" fontWeight="semibold">
                {children}
              </Text>
            ),
          }}
        >
          {variantContent}
        </ReactMarkdown>

        <Flex direction="column" maxW={{ base: "100%", md: "300px" }}>
          <Box display={{ base: "block", md: "none" }} alignSelf="center" maxW="45%" mb="6">
            {posterImage}
          </Box>

          <HubSpotForm
            displayedFields={["firstname", "lastname"]}
            formId={hubSpotFormId}
            label="Download"
            successContent={
              success && (
                <Text textStyle="caption1">
                  Thank you! If your download doesn&apos;t start immediately, please{" "}
                  <Link variant="inline" href={downloadFileUrl}>
                    click here
                  </Link>
                  .
                </Text>
              )
            }
            onSubmit={onFormSubmit}
          />
        </Flex>
      </Box>

      <Box minW="30%" display={{ base: "none", md: "block" }}>
        {posterImage}
      </Box>
    </Flex>
  );
}

/**
 * A legacy component that downloads
 * [The Complete Guide to Reverse ETL](https://hightouch.sanity.studio/production/structure/marketing;resources;resource;fab4f8c9-84ac-44df-b1aa-d0c918ee5bec)
 * resource. Still referenced in a couple of old blog posts. Can be removed if those get updated.
 */
export function DownloadReverseEtlWhitepaper() {
  return (
    <DownloadWhitepaper
      variant="long"
      hubSpotFormId="f66b4395-70da-4612-afd0-87a66c601c87"
      title="Reverse ETL Whitepaper"
      fileUrl="https://cdn.sanity.io/files/pwmfmi47/production/65b94675c25e2791a7ed127094155462ed035725.pdf?dl=hightouch-reverse-etl-whitepaper.pdf"
      fileName="hightouch-reverse-etl-whitepaper.pdf"
      posterImageUrl="https://cdn.sanity.io/images/pwmfmi47/production/8f08ee07118963345f3c5447aac0c1f73a780b31-1224x1584.jpg"
      variantContent={`## Reverse ETL Whitepaper
**Download our whitepaper to learn:**
- How to leverage Reverse ETL to eliminate data silos and activate your data warehouse to improve business outcomes
- Specific use cases for Reverse ETL and how it fits into the modern data stack
- New ways all teams can meet goals with better access to data in their operational tools
- The core must-haves in a Reverse ETL solution`}
    />
  );
}
